import { Component, ElementRef, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputFieldComponent } from '../../../../components/form/input-field/input-field.component';
import { TextAreaFieldComponent } from '../../../../components/form/textarea-field/textarea-field.component';
import { ProjectsService } from '../../../../services/api/projects.service';
import { AlertNotificationService } from '../../../../services/alert-notification.service';
import { HttpParams } from '@angular/common/http';
import { CommonService } from 'src/app/services/common.service';
import { ConfirmationDialogService } from '../../../../services/confirmation-dialog.service';
import { AccessControlService } from '../../../../services/access-control.service';
import { MainLayoutService } from '../../../../services/main-layout.service';

const params = new HttpParams().set('size', '9999');

@Component({
  selector: 'projects-documents-requested-to-client',
  imports: [
    CommonModule,
    ReactiveFormsModule,
    InputFieldComponent,
    TextAreaFieldComponent
  ],
  templateUrl: './projects-documents-requested-to-client.component.html',
  styleUrl: './projects-documents-requested-to-client.component.scss'
})
export class ProjectsDocumentsRequestedToClientComponent implements OnInit {
  @Input() projectId?: string;
  @Output() onDocumentsRequestedUpdated = new EventEmitter<any>();
  @ViewChild('fileInput') fileInput?: ElementRef<HTMLInputElement>;
  @ViewChild('formCard') formCard?: ElementRef<HTMLDivElement>;

  documentsForm: FormGroup;
  documents: any[] = [];
  selectedDocumentId: number | null = null;
  selectedFile: File | null = null;
  isEditing = false;
  editingDocumentId: number | null = null;
  isUploadFile = false;
  canDeleteDocument = false;
  canCreateDocument = false;
  canSendRequestToClient = false;

  constructor(
    private fb: FormBuilder,
    private projectsService: ProjectsService,
    private alertNotificationService: AlertNotificationService,
    private commonService: CommonService,
    private confirmationDialogService: ConfirmationDialogService,
    private mainLayoutService: MainLayoutService,
    private accessControlService: AccessControlService
  ) {
    this.documentsForm = this.fb.group({
      documentNeeded: ['', Validators.required],
      comment: ['']
    });

  }

  ngOnInit(): void {
    this.mainLayoutService.activeProfile$.subscribe(profileCode => {
      this.canDeleteDocument = this.accessControlService.hasFeatureActionAccess(profileCode as string | null, 'PROJECTS_CLIENTS_REQUESTED_DOCUMENTS', 'delete');
      this.canCreateDocument = this.accessControlService.hasFeatureActionAccess(profileCode as string | null, 'PROJECTS_CLIENTS_REQUESTED_DOCUMENTS_CREATE', 'modify');
      this.canSendRequestToClient = this.accessControlService.hasFeatureActionAccess(profileCode as string | null, 'PROJECTS_CLIENTS_REQUESTED_DOCUMENTS_SEND_REQUEST', 'modify');
    });

    this.loadDocuments();
  }

  loadDocuments(): void {
    if (this.projectId) {
      this.projectsService.listDocumentsRequestedToClient(Number(this.projectId))
        .subscribe({
          next: (response) => {
            this.documents = response.data.projectCustomerDocuments;
            this.onDocumentsRequestedUpdated.emit(this.documents);
          },
          error: (error) => {
            this.alertNotificationService.error('Error loading documents');
            console.error('Error:', error);
          }
        });
    }
  }

  startEdit(document: any): void {
    this.isEditing = true;
    this.editingDocumentId = document.projectCustomerDocumentId;
    this.documentsForm.patchValue({
      documentNeeded: document.documentNeeded,
      comment: document.comment
    });
    this.scrollToForm();
  }

  cancelEdit(): void {
    this.isEditing = false;
    this.editingDocumentId = null;
    this.resetForm();
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files.length > 0) {
      this.selectedFile = input.files[0];
    } else {
      this.selectedFile = null;
    }
  }

  onSubmit(): void {
    if (this.projectId) {
      if (this.isUploadFile && this.editingDocumentId && this.selectedFile) {
        const formDataToSend = new FormData();
        const data = {
          documentNeeded: this.documentsForm.getRawValue().documentNeeded,
          comment: this.documentsForm.getRawValue().comment ?? ''
        };

        formDataToSend.append('file', this.selectedFile);
        formDataToSend.append('data', JSON.stringify(data));

        this.projectsService.updateDocumentRequestedToClient(
          Number(this.projectId),
          this.editingDocumentId,
          formDataToSend
        ).subscribe({
          next: () => {
            this.alertNotificationService.success('Document uploaded successfully');
            this.loadDocuments();
            this.cancelCustomerUploadFile();
          },
          error: (error) => {
            this.alertNotificationService.error('Error uploading document');
            console.error('Error:', error);
          }
        });
      }
      else if (this.isEditing && this.editingDocumentId) {
        if (!this.documentsForm.valid) {
          this.documentsForm.markAllAsTouched();
          this.alertNotificationService.error('Please fill all required fields');
          return;
        }

        const updateData = {
          documentNeeded: this.documentsForm.value.documentNeeded,
          comment: this.documentsForm.value.comment ?? ''
        };
        
        const formDataToSend = new FormData();
        formDataToSend.append('data', JSON.stringify(updateData));

        this.projectsService.updateDocumentRequestedToClient(
          Number(this.projectId),
          this.editingDocumentId,
          formDataToSend
        ).subscribe({
          next: () => {
            this.alertNotificationService.success('Document updated successfully');
            this.loadDocuments();
            this.cancelEdit();
          },
          error: (error) => {
            this.alertNotificationService.error('Error updating document');
            console.error('Error:', error);
          }
        });
      }
      else if (!this.isEditing && !this.isUploadFile) {
        if (!this.documentsForm.valid) {
          this.documentsForm.markAllAsTouched();
          this.alertNotificationService.error('Please fill all required fields');
          return;
        }

        const formDataToSend = new FormData();
        const data = {
          documentNeeded: this.documentsForm.value.documentNeeded,
          comment: this.documentsForm.value.comment ?? ''
        };

        if (this.selectedFile && this.selectedFile.size > 0) {
          formDataToSend.append('file', this.selectedFile);
        }
        formDataToSend.append('data', JSON.stringify(data));

        this.projectsService.createDocumentRequestedToClient(Number(this.projectId), formDataToSend)
          .subscribe({
            next: () => {
              this.alertNotificationService.success('Document added successfully');
              this.loadDocuments();
              this.resetForm();
            },
            error: (error) => {
              this.alertNotificationService.error('Error adding document');
              console.error('Error:', error);
            }
          });
      }
    }
  }

  resetForm(): void {
    this.documentsForm.reset();
    this.selectedFile = null;
    this.isEditing = false;
    this.isUploadFile = false;
    this.editingDocumentId = null;
    
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  downloadFile(document: any): void {
    if (document.url) {
      this.projectsService.GET(document.url).subscribe({
        next: (response) => {
          window.open(response.data.attachment.publicUrl, '_blank');
        },
        error: (error) => {
          this.alertNotificationService.error('Error downloading file');
          console.error('Error:', error);
        }
      });
    }
  }

  openDeleteConfirmation(documentId: number): void {
    this.selectedDocumentId = documentId;
    this.confirmationDialogService.open(
      'Are you sure you want to delete this document?',
      () => this.onDeleteConfirmed()
    );
  }

  onDeleteConfirmed(): void {
    if (this.selectedDocumentId && this.projectId) {
      this.projectsService.deleteDocumentRequestedToClient(Number(this.projectId), this.selectedDocumentId)
        .subscribe({
          next: () => {
            this.alertNotificationService.success('Document deleted successfully');
            this.loadDocuments();
          },
          error: (error) => {
            this.alertNotificationService.error('Error deleting document');
            console.error('Error:', error);
          }
        });
    }
    this.selectedDocumentId = null;
  }

  startCustomerUploadFile(document: any): void {
    this.isUploadFile = true;
    this.editingDocumentId = document.projectCustomerDocumentId;
    this.documentsForm.patchValue({
      documentNeeded: document.documentNeeded,
      comment: document.comment
    });
    this.documentsForm.get('documentNeeded')?.disable();
    if (this.canCreateDocument) {
      this.documentsForm.get('comment')?.disable();
    } else {
      this.documentsForm.get('comment')?.enable();
    }
    this.scrollToForm();
  }

  cancelCustomerUploadFile(): void {
    this.isUploadFile = false;
    this.editingDocumentId = null;
    this.documentsForm.get('documentNeeded')?.enable();
    this.documentsForm.get('comment')?.enable();
    this.documentsForm.reset();
    this.selectedFile = null;
    
    if (this.fileInput?.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  sendRequestToClient(): void {
    this.projectsService.sendDocumentRequestedToClient(Number(this.projectId)).subscribe({
      next: () => {
        this.alertNotificationService.success('Request sent to client successfully');
      },
      error: (error) => {
        this.alertNotificationService.error('Error sending request to client');
        console.error('Error:', error);
      }
    });
  }

  getDescription(): FormControl { return this.getDocumentsFormControl('documentNeeded'); }
  getComment(): FormControl { return this.getDocumentsFormControl('comment'); }

  getDocumentsFormControl(name: string): FormControl { return this.documentsForm.get(name) as FormControl; }

  scrollToForm(): void {
    setTimeout(() => {
      if (this.formCard?.nativeElement) {
        this.formCard.nativeElement.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'start' 
        });
      }
    }, 100);
  }

  formatDate(dateString: string | null | undefined): string {
    if (!dateString) {
      return '';
    }
    try {
     return this.commonService.formatDate(new Date(dateString));
    } catch (error) {
      return '';
    }
  }

  getRequestedUserName(doc: any): string {
    if (doc && doc.requestedUser) {
      const firstName = doc.requestedUser.firstName || '';
      const lastName = doc.requestedUser.lastName || '';
      return `${firstName} ${lastName}`.trim();
    }
    return '';
  }

  getUploadedUserName(doc: any): string {
    if (doc && doc.uploadedUser) {
      const firstName = doc.uploadedUser.firstName || '';
      const lastName = doc.uploadedUser.lastName || '';
      return `${firstName} ${lastName}`.trim();
    }
    return '';
  }

  getDocumentDetail(doc: any): any {
    return doc || null;
  }
}
